import React from 'react';
import { Link } from 'react-router-dom';

import { Alert, Modal } from 'antd';
import { ModalProps } from 'antd/lib/modal';
import Message from 'antd/lib/message';

import * as Yup from 'yup';

import { phoneValidator, simpleStringValidator } from '@common/react/utils/validationHelpers';
import FormikField from '@common/react/components/Forms/FormikField/FormikField';
import { FormikPhoneControl } from '@common/react/components/Forms/FormikPhoneControl/FormikPhoneControl';
import { ConfirmationCodeInput } from '@common/react/components/Forms/ConfirmationCodeInput/ConfirmationCodeInput';
import { RequestButton } from '@common/react/components/UI/LoadingButton/LoadingButton';
import { BaseParams } from '@common/react/objects/BaseParams';
import { ItemProvider } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemEditor } from '@common/react/components/Core/ItemEditor/ItemEditor';

interface ComponentProps {
	className?: string;
	finalAction?: (info?: { phone?: string }) => void;
	disabledConfirm?: boolean;
	sendConfirmPhoneRequestName?: string;
	sendConfirmPhoneParams?: BaseParams;
	phoneConfirmationCodeRequest?: string;
	sendPhoneConfirmationCodeRequest?: string;
	confirmText?: React.ReactNode;
	changeText?: React.ReactNode;
	defaultConfirmVisible?: boolean;
	modalOptions?: ModalProps;
	systemAdministrationLink?: string;
	onClickSystemAdministrationLink?: (e, setVisible) => void;
	savedPhoneNumber?: string;
}

interface ChangeInfoFormik {
	id: number;
	phoneNumber: string;
}

const initialValues: ChangeInfoFormik = {
	id: -1,
	phoneNumber: '',
};

const validationSchema = Yup.object().shape({
	phoneNumber: phoneValidator,
});

const validationSchemaConfirm = Yup.object().shape({
	code: simpleStringValidator,
});

const ChangeInfoModal: React.FC<ComponentProps> = ({
	className,
	finalAction = () => Message.info('Success!'),
	disabledConfirm,
	sendConfirmPhoneParams = {},
	sendConfirmPhoneRequestName = 'sendPhoneConfirmationCode',
	phoneConfirmationCodeRequest = 'phoneConfirmationCode',
	sendPhoneConfirmationCodeRequest = 'sendPhoneConfirmationCode',
	confirmText = 'Confirm Phone',
	changeText = 'Change Phone',
	defaultConfirmVisible = false,
	modalOptions,
	systemAdministrationLink,
	onClickSystemAdministrationLink,
	savedPhoneNumber = '',
}) => {
	const [visible, setVisible] = React.useState<boolean>(false);
	const [visibleConfirm, setVisibleConfirm] = React.useState<boolean>(defaultConfirmVisible);
	const [phoneNumber, setPhoneNumber] = React.useState<string>(savedPhoneNumber);

	React.useEffect(() => {
		if (savedPhoneNumber !== phoneNumber) {
			setPhoneNumber(savedPhoneNumber);
		}
	}, [savedPhoneNumber]);

	return <div className={className ?? 'clearfix'}>
		<RequestButton
			requestType={sendConfirmPhoneRequestName}
			requestProps={sendConfirmPhoneParams}
			className="btn-group ratio-group btn-default btn"
			type="button"
			title="Confirm phone"
			disabled={disabledConfirm}
			onSuccess={() => {
				// Modal.success({ content: 'An phone confirmation message has been sent to your new phone.' });
				setVisibleConfirm(true);
			}}
		>
			{confirmText}
		</RequestButton>
		<button
			title="Change Phone"
			className="btn-group ratio-group btn-default btn"
			type="button"
			onClick={() => setVisible(true)}
		>
			{changeText}
		</button>
		<Modal
			title="Change Phone"
			open={visible}
			onCancel={() => {
				setVisible(false);
			}}
			destroyOnClose
			width={500}
			footer={null}
		>
			<div className="clearfix">
				<Alert
					className="pull-left"
					showIcon
					type="info"
					style={{ marginBottom: 10 }}
					message={<div style={{ display: 'inline-block', marginLeft: 5 }}>
						You need access to your current phone to change it.
						<br />
						If you don't -
						{' '}
						{systemAdministrationLink ? <Link
							to={systemAdministrationLink}
							onClick={(e) => onClickSystemAdministrationLink?.(e, setVisible)}
						>
							Contact with support
						</Link> : 'Contact with support'}
					</div>}
				/>
			</div>
			<ItemProvider
				id={-1}
				add={initialValues}
				type=""
				saveRequest={sendPhoneConfirmationCodeRequest}
				validationSchema={validationSchema}
			>
				<ItemEditor<ChangeInfoFormik>
					withButtons
					readonly={false}
					formikProps={{ enableReinitialize: true }}
					successMessage=""
					afterSubmit={(values) => {
						// Modal.success({ content: 'An phone confirmation message has been sent to your new phone.' });
						setPhoneNumber(values?.phoneNumber ?? '');
						setVisibleConfirm(true);
					}}
					saveText="Send confirmation Code"
					edit={() => <>
						<div className="row">
							<FormikField
								containerClassName="col-sm-12 form-group"
								fieldName="phoneNumber"
								title="New phone number"
								render={(fieldProps) => <>
									<FormikPhoneControl
										fieldProps={fieldProps}
										placeholder="+1 (___) ___-____"
									/>
								</>}
							/>
						</div>
					</>}
				/>
			</ItemProvider>
		</Modal>
		<Modal
			title="Confirmation Code"
			open={visibleConfirm}
			onCancel={() => {
				setVisibleConfirm(false);
			}}
			destroyOnClose
			width={500}
			footer={null}
			{...modalOptions}
		>
			<div className="clearfix">
				<Alert
					className="pull-left"
					showIcon
					type="info"
					style={{ marginBottom: 10 }}
					message={<div style={{ display: 'inline-block', marginLeft: 5 }}>
						An phone number confirmation message has been sent to your current phone number.
					</div>}
				/>
			</div>
			<ItemProvider<{ phoneNumber, id, code, newPhoneNumber }>
				id={-1}
				add={{
					code: '',
					...(savedPhoneNumber === phoneNumber ? { phoneNumber } : { newPhoneNumber: phoneNumber }),
				}}
				type=""
				saveRequest={phoneConfirmationCodeRequest}
				validationSchema={validationSchemaConfirm}
			>
				<ItemEditor<{ id, code }>
					withButtons
					readonly={false}
					formikProps={{ enableReinitialize: true }}
					successMessage=""
					afterSubmit={(values) => {
						setVisibleConfirm(false);
						setVisible(false);
						finalAction && finalAction({ phone: phoneNumber });
					}}
					saveText="Confirm"
					edit={() => <div className="row">
						<FormikField
							containerClassName="col-sm-12 form-group mt10"
							fieldName="code"
							title=""
							render={(fieldProps) =>
								<div className="text-center">
									<label htmlFor="code">Code</label>
									<ConfirmationCodeInput
										fieldProps={fieldProps}
									/>
								</div>
							}
						/>
					</div>}
				/>
			</ItemProvider>
		</Modal>
	</div>;
};

export default ChangeInfoModal;
